import type { ArrivalStep } from '@/api/types/ProviderApi'
import { ArrivalDots, ArrivalGuide } from '@/components/VisitPage/ArrivalGuide'
import { CardRule } from '@/components/VisitPage/CardRule'
import { Glyph } from '@/components/svgs/Glyph'
import { colors } from '@/constants/colors'
import { analytics } from '@/lib/analytics'
import { useDiscColor } from '@/ui/core/card-ground'
import { Pressable } from '@/ui/core/pressable'
import { Sheet, SheetTitle } from '@/ui/core/sheet'
import { Text } from '@/ui/core/typography'
import { useState } from 'react'
import { View } from 'react-native'

/**
 * The studio's own way in, behind a row.
 *
 * It was the tallest thing on the visit page and the only thing anyone opened that page for on
 * the day — a photograph per step, which is a page's worth of height spent on something a member
 * wants for ninety seconds on a pavement. As a row it costs a line, and what it opens is the
 * whole guide over a page that stays where it was.
 *
 * It was briefly a section of the page instead, photographs at full width. At the studio's own
 * limits — `ops-web` allows a step 50 characters of title and 200 of description — that section
 * is 250 points tall and its caption block reflows as you swipe, so what to expect and the forms
 * moved down the page under a thumb. A row is one line, and the sheet has nothing below it to
 * shove.
 *
 * Only where the studio has steps. They are authored per studio in the ops console, because
 * "through the lobby and up to six" is true of one building and nonsense about another.
 */
export const ArrivalRow = ({ steps }: { steps: ArrivalStep[] }) => {
	const [open, setOpen] = useState(false)
	const [at, setAt] = useState(0)
	const disc = useDiscColor()

	if (steps.length === 0) {
		return null
	}

	return (
		<>
			<Pressable
				onPress={() => {
					analytics.trackClick('visit-arrival-guide')
					// Back to the pavement. The sheet's children stay mounted behind a closed
					// `Modal`, so a member who swiped to the lift lobby and shut the sheet used to
					// reopen it there — halfway along a walk they had not started yet.
					setAt(0)
					setOpen(true)
				}}
				accessibilityRole="button"
				accessibilityLabel={`Arrival instructions. ${steps.length} steps`}
				className="flex-row items-center px-4 py-4"
				style={{ gap: 12 }}
			>
				<CardRule />
				<View
					className="h-10 w-10 items-center justify-center rounded-full"
					style={{ backgroundColor: disc }}
				>
					{/* Footprints: the mark describes the walk, which is what the instructions
					    are for. A photograph was tried — the instructions *are* photographs, and
					    the frame said so — but at nineteen point a picture frame is a small
					    rounded square with detail inside it, which is what the pass's QR is two
					    rows up. The footprints are the only mark in this card that could not be
					    mistaken for another. */}
					<Glyph name="steps" size={20} color={colors.ink} />
				</View>
				{/* "Arrival Instructions", where it said "When You Arrive" and then "Arrival
				    Steps". The first was a moment in time, beside two rows that are things — an
				    address and a pass; the second read as a count of something. This is the name
				    of what the row opens. No second line: the count was tried there, and a row
				    whose only job is to open a short gallery does not need to negotiate. */}
				<Text size="body0" weight="bold" className="flex-1">
					Arrival Instructions
				</Text>
				<Glyph name="chevron" size={14} color={colors.ink} />
			</Pressable>
			<Sheet open={open} onClose={() => setOpen(false)}>
				<View className="pt-2">
					<SheetTitle glyph="steps">Arrival Instructions</SheetTitle>
					<View className="px-4" style={{ paddingBottom: 10 }}>
						<ArrivalGuide steps={steps} index={at} onIndex={setAt} />
						{/* The dots at the foot of the sheet, under the words for the step they
						    are pointing at. They were at the title's end, which made a heading
						    hold a control, and then between the title and the photographs, which
						    put the counter before the thing it counts.

						    Twenty-eight clear of the description: a row of dots four points under
						    a paragraph reads as its punctuation. They are drawn here rather than
						    inside the guide because the guide is controlled — one component owns
						    the position, and it is this one. */}
						{steps.length > 1 && (
							<View style={{ marginTop: 28 }}>
								<ArrivalDots count={steps.length} at={at} onPick={setAt} />
							</View>
						)}
					</View>
				</View>
			</Sheet>
		</>
	)
}
